import { useState, useEffect, useMemo } from "react";
import { toast } from "sonner";
import { api, errorMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Merge, AlertTriangle, ArrowRight, Loader2 } from "lucide-react";

export const AgruparEquivalentesDialog = ({ open, maestraId, maestraNombre, onClose, onDone }) => {
  const [cargando, setCargando] = useState(false);
  const [grupos, setGrupos] = useState([]);
  const [acept, setAcept] = useState(() => new Set());
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (!open || !maestraId) return;
    setCargando(true); setGrupos([]); setAcept(new Set());
    api.get(`/plantillas/${maestraId}/agrupar-equivalentes/preview`)
      .then(({ data }) => {
        setGrupos(data.grupos || []);
        setAcept(new Set((data.grupos || []).map((g) => g.grupo_id))); // todos aceptados por defecto
      })
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [open, maestraId]);

  const toggle = (gid) => setAcept((p) => { const n = new Set(p); n.has(gid) ? n.delete(gid) : n.add(gid); return n; });

  const totalAceptados = useMemo(() => grupos.filter((g) => acept.has(g.grupo_id)).length, [grupos, acept]);

  const aplicar = async () => {
    const sel = grupos.filter((g) => acept.has(g.grupo_id)).map((g) => g.hija_ids);
    if (sel.length === 0) { toast.error("No hay grupos marcados para fusionar"); return; }
    setGuardando(true);
    try {
      const { data } = await api.post(`/plantillas/${maestraId}/agrupar-equivalentes/aplicar`, { grupos: sel });
      toast.success(`${data.fusionadas} grupo(s) fusionado(s) · ${data.eliminadas} hija(s) eliminada(s)`);
      onClose(); onDone && onDone();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setGuardando(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="agrupar-equivalentes-dialog" className="bg-white max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <Merge size={18} className="text-indigo-600" /> Agrupar plantillas equivalentes
          </DialogTitle>
          <p className="text-xs text-muted-foreground">
            Hijas de <b>{maestraNombre}</b> que comparten exactamente los mismos repuestos se pueden unir en una sola
            que cubra todos sus modelos. Solo hijas de esta maestra; aceptá u omití cada grupo.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          {cargando && (
            <p className="py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Buscando plantillas equivalentes…
            </p>
          )}
          {!cargando && grupos.length === 0 && (
            <p data-testid="agrupar-sin-grupos" className="py-10 text-center text-sm text-muted-foreground">
              No hay hijas equivalentes para agrupar: cada plantilla generada tiene un conjunto de repuestos distinto.
            </p>
          )}
          {grupos.map((g) => {
            const on = acept.has(g.grupo_id);
            return (
              <div key={g.grupo_id} data-testid={`agrupar-grupo-${g.grupo_id}`}
                className={`rounded-sm border p-3 ${on ? "border-indigo-200 bg-indigo-50/30" : "border-slate-200 opacity-60"}`}>
                <label className="flex items-center gap-2 cursor-pointer mb-2">
                  <Checkbox data-testid={`agrupar-sel-${g.grupo_id}`} checked={on} onCheckedChange={() => toggle(g.grupo_id)} />
                  <span className="text-sm font-semibold flex-1">
                    Fusionar {g.hijas.length} plantillas en una
                  </span>
                  {g.cotizaciones_afectadas > 0 && (
                    <span data-testid={`agrupar-cot-aviso-${g.grupo_id}`}
                      className="flex items-center gap-1 text-[10px] font-semibold text-amber-800 bg-amber-100 border border-amber-300 px-1.5 py-0.5 rounded-sm">
                      <AlertTriangle size={11} /> {g.cotizaciones_afectadas} cotización(es) referencian estas hijas
                    </span>
                  )}
                </label>

                <div className="pl-6 space-y-2 text-xs">
                  <div className="space-y-1">
                    {g.hijas.map((h) => (
                      <div key={h.id} data-testid={`agrupar-hija-${h.id}`} className="flex items-start gap-2 text-slate-600">
                        <span className="text-slate-400 mt-0.5">•</span>
                        <span className="flex-1">
                          {h.nombre}
                          <span className="text-slate-400"> — {h.modelos.map((m) => m.nombre_mostrar).join(", ") || "sin modelos"}</span>
                          {h.cotizaciones_ref > 0 && (
                            <span className="ml-1 text-[9px] uppercase font-semibold text-amber-700 bg-amber-100 px-1 rounded-sm">
                              {h.cotizaciones_ref} cot.
                            </span>
                          )}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="flex items-start gap-2 bg-white border border-indigo-100 rounded-sm p-2">
                    <ArrowRight size={13} className="text-indigo-500 mt-0.5 shrink-0" />
                    <div className="flex-1">
                      <p className="font-semibold text-slate-800" data-testid={`agrupar-resultado-${g.grupo_id}`}>{g.nombre_sugerido}</p>
                      <p className="text-[11px] text-teal-700 mt-0.5">
                        Cubrirá: {g.modelos_union.map((m) => m.nombre_mostrar).join(", ")}
                      </p>
                      {g.items.length > 0 && (
                        <p className="text-[11px] text-slate-500 mt-1">
                          Repuestos ({g.items.length}): {g.items.map((it) => it.codigo || it.nombre).join(", ")}
                        </p>
                      )}
                      <p className="text-[11px] text-slate-500 mt-0.5">
                        Mano de obra: <b>₡{Number(g.mano_de_obra_maestra || 0).toLocaleString("es-CR")}</b>
                        {g.frt_maestra != null && <> · FRT <b>{g.frt_maestra}</b></>} (de la maestra)
                        {g.mano_obra_difiere && (
                          <span className="ml-1 text-amber-700">· las hijas tenían mano de obra distinta; se usa la de la maestra</span>
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="shrink-0 gap-2">
          <Button variant="outline" data-testid="agrupar-cancelar" onClick={onClose}>Cancelar</Button>
          <Button data-testid="agrupar-aplicar" onClick={aplicar} disabled={guardando || totalAceptados === 0}
            className="bg-indigo-600 hover:bg-indigo-700 text-white">
            {guardando ? "Fusionando…" : `Fusionar (${totalAceptados})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
